import { useState, useCallback, useContext, useEffect } from 'react';
import { AppContext } from 'views/App/context';

export interface OrgProof {
  id: string;
  standardId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  notes: string;
  uploadedAt: string;
  uploadedByName: string;
}

function storageKey(accreditationBodyId: string, versionId: string, standardId: string) {
  return `awb-org-proofs-${accreditationBodyId}-${versionId}-${standardId}`;
}

function loadProofs(key: string): OrgProof[] {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveProofs(key: string, proofs: OrgProof[]) {
  try {
    sessionStorage.setItem(key, JSON.stringify(proofs));
  } catch {
    // silent fail
  }
}

export interface UseProofsOfComplianceOrgReturn {
  proofs: OrgProof[];
  addProof: (file: { name: string; size: number; type: string }, notes: string, authorName: string) => OrgProof;
  removeProof: (id: string) => void;
  updateProofNotes: (id: string, notes: string) => void;
}

/**
 * Org-side proofs of compliance for the awbPremiumDemo experience.
 * State is persisted to sessionStorage (demo only — no real upload API).
 */
export default function useProofsOfComplianceOrg(
  accreditationBodyId: string,
  versionId: string,
  standardId: string
): UseProofsOfComplianceOrgReturn {
  const { isFeatureEnabled } = useContext(AppContext);
  const isEnabled = isFeatureEnabled('awbPremiumDemo');
  const key = storageKey(accreditationBodyId, versionId, standardId);

  const [proofs, setProofsRaw] = useState<OrgProof[]>(() => loadProofs(key));

  useEffect(() => {
    setProofsRaw(loadProofs(key));
  }, [key]);

  const setProofs = useCallback(
    (updater: (prev: OrgProof[]) => OrgProof[]) => {
      setProofsRaw((prev) => {
        const next = updater(prev);
        saveProofs(key, next);
        return next;
      });
    },
    [key]
  );

  const addProof = useCallback(
    (file: { name: string; size: number; type: string }, notes: string, authorName: string): OrgProof => {
      const proof: OrgProof = {
        id: `proof-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        standardId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        notes,
        uploadedAt: new Date().toISOString(),
        uploadedByName: authorName,
      };
      setProofs((prev) => [...prev, proof]);
      return proof;
    },
    [setProofs, standardId]
  );

  const removeProof = useCallback(
    (id: string) => setProofs((prev) => prev.filter((p) => p.id !== id)),
    [setProofs]
  );

  const updateProofNotes = useCallback(
    (id: string, notes: string) =>
      setProofs((prev) => prev.map((p) => (p.id === id ? { ...p, notes } : p))),
    [setProofs]
  );

  if (!isEnabled) {
    return {
      proofs: [],
      addProof: () => ({} as OrgProof),
      removeProof: () => {},
      updateProofNotes: () => {},
    };
  }

  return { proofs, addProof, removeProof, updateProofNotes };
}
